import { type ComponentType, Fragment, type HTMLAttributes, type HTMLProps, type MouseEventHandler, type ReactNode, useMemo } from 'react';
import type { Cell, Row } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { useWriteGatedInteraction } from '@interxfi/design-system/access-context';
import { type Interaction, Interactive } from '@interxfi/design-system/util';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { type RowLevelInteractionBuilder, TableRowContextProvider } from '../../row-context';
import { TD } from '../td/td';
import styles from './tr.module.scss';

const propagationStopper: MouseEventHandler = (e) => e.stopPropagation();

function mapInteractionToRowLevelAction(interaction: Interaction | undefined): Interaction | undefined {
  switch (interaction?.type) {
    case 'link': {
      if (!interaction.link.to) {
        return undefined;
      }

      return {
        ...interaction,
        link: {
          ...interaction.link,
          children: null,
          className: styles.rowAction,
          onClick: (e) => {
            propagationStopper(e);
            interaction.link.onClick?.(e);
          },
          tabIndex: -1,
        },
      };
    }
    case 'drawer':
      return {
        ...interaction,
        drawer: {
          ...interaction.drawer,
          props: {
            ...interaction.drawer.props,
            trigger: <button aria-label="Expand row details" className={styles.rowAction} type="button" />,
          },
        },
      };
    case 'modal':
      return {
        ...interaction,
        modal: {
          ...interaction.modal,
          props: {
            ...interaction.modal.props,
            trigger: <button aria-label="Expand row details" className={styles.rowAction} type="button" />,
          },
        },
      };
    case 'button':
      return {
        ...interaction,
        button: {
          ...interaction.button,
          className: styles.rowAction,
          tabIndex: -1,
          onClick: (e) => {
            propagationStopper(e);
            interaction.button.onClick?.(e);
          },
        },
      };
    case 'styledButton':
      return {
        ...interaction,
        styledButton: {
          ...interaction.styledButton,
          className: styles.rowAction,
          tabIndex: -1,
          onClick: (e) => {
            propagationStopper(e);
            interaction.styledButton.onClick?.(e);
          },
        },
      };
    case 'styledButtonLink':
      return {
        ...interaction,
        styledButtonLink: {
          ...interaction.styledButtonLink,
          className: styles.rowAction,
          tabIndex: -1,
          onClick: (e) => {
            propagationStopper(e);
            interaction.styledButtonLink.onClick?.(e);
          },
        },
      };
    case 'dropdownMenu': {
      return {
        ...interaction,
        dropdownMenu: {
          ...interaction.dropdownMenu,
          props: {
            ...interaction.dropdownMenu.props,
            className: styles.rowAction,
            containerClassName: styles.rowAction,
            tabIndex: -1,
            activator: <button aria-label="Expand row menu" className={styles.rowAction} type="button" />,
          },
        },
      };
    }
    case 'tooltip':
    case undefined:
      return undefined;
    default: {
      exhaustiveCheck(interaction);
      return undefined;
    }
  }
}

function TableBody({ children, className, ...rest }: HTMLProps<HTMLTableSectionElement>) {
  return (
    <tbody className={clsx(styles.tbody, className)} {...rest}>
      {children}
    </tbody>
  );
}

interface TRProps<T extends object> extends HTMLAttributes<HTMLTableRowElement> {
  canExpand: boolean;
  cells: Cell<T, unknown>[];
  defaultCellRenderWhenEmpty?: ReactNode;
  isExpanded: boolean;
  row: Row<T>;
  rowLevelInteractionBuilder: RowLevelInteractionBuilder<T> | undefined;
  showSkeleton: boolean | undefined;
  SubRowComponent?: ComponentType<T>;
  totalRows: number;
}

export function TR<TData extends object>({
  canExpand,
  cells,
  defaultCellRenderWhenEmpty,
  isExpanded,
  row,
  rowLevelInteractionBuilder,
  showSkeleton,
  SubRowComponent,
  totalRows,
  ...rest
}: TRProps<TData>) {
  // 'use no memo' directive is required for now because react-table v8 is not compatible with React 19's compiler just yet
  'use no memo';

  const builtRowLevelInteraction = useMemo(() => {
    if (!rowLevelInteractionBuilder || !row.original || (typeof row.original === 'object' && !Object.keys(row.original || {}).length)) {
      return undefined;
    }

    return rowLevelInteractionBuilder(row.original);
  }, [rowLevelInteractionBuilder, row.original]);

  const rowLevelInteraction = useWriteGatedInteraction(builtRowLevelInteraction);

  const mappedRowLevelInteraction = useMemo(() => mapInteractionToRowLevelAction(rowLevelInteraction), [rowLevelInteraction]);

  const ParentElement = canExpand && SubRowComponent ? TableBody : Fragment;

  return (
    <TableRowContextProvider rowLevelInteraction={rowLevelInteraction}>
      <ParentElement>
        <tr
          aria-expanded={isExpanded}
          aria-level={canExpand ? 1 : undefined}
          aria-posinset={row.index + 1}
          aria-setsize={totalRows}
          className={clsx(styles.tr, isExpanded && styles.isExpanded)}
          {...rest}
        >
          {cells.map((cell) => (
            <TD<TData>
              cell={cell}
              defaultCellRenderWhenEmpty={defaultCellRenderWhenEmpty}
              key={`${cell.id}-${isExpanded}`}
              showSkeleton={showSkeleton}
            />
          ))}

          {mappedRowLevelInteraction ? (
            <td className={styles.rowActionContainer} tabIndex={-1}>
              <Interactive {...mappedRowLevelInteraction} />
            </td>
          ) : null}
        </tr>

        {SubRowComponent && isExpanded ? (
          <tr aria-level={canExpand ? 2 : undefined} aria-posinset={1} aria-setsize={1} className={styles.subRow}>
            <TD<TData> isSubRowCell showSkeleton={showSkeleton}>
              <SubRowComponent {...row.original} />
            </TD>
          </tr>
        ) : null}
      </ParentElement>
    </TableRowContextProvider>
  );
}
